"use client"

import * as React from "react"
import { useLocation } from "react-router"
import { PageHeader } from "@/components/page-header"
import { EVAL_DASHBOARD_KPI_A11Y_CLASS } from "@/lib/evaluation-dashboard-a11y"
import { EvaluationDashboardLifecycleView } from "./evaluation-dashboard-lifecycle-view"
import { EvaluationDashboardActionView } from "./evaluation-dashboard-action-view"
import { EvaluationDashboardAnalyticView } from "./evaluation-dashboard-analytic-view"
import { EvaluationDashboardQualityView } from "./evaluation-dashboard-quality-view"
import { EvaluationDashboardOperationsView } from "./evaluation-dashboard-operations-view"
import { EvaluationDashboardOperationsV2View } from "./evaluation-dashboard-operations-v2-view"
import { QualityTermSelector } from "./quality/quality-term-bar"
import { AllTermsSection } from "./all-terms-section"
import {
  getDashboardTermSlice,
} from "@/lib/evaluation-dashboard-context"
import { useEvaluationDashboardApproach } from "@/hooks/use-evaluation-dashboard-approach"
import { useAskLeoPageContext } from "@/components/ask-leo-context"
import { cn } from "@/lib/utils"

export function EvaluationDashboard() {
  const { search } = useLocation()
  const approach = useEvaluationDashboardApproach()
  const termSlice = React.useMemo(() => getDashboardTermSlice(search), [search])
  const {
    showNoTerms,
    currentTerm,
    lastTerm,
    pastTerms,
    upcomingTerms,
    futureTerms,
    allTerms,
  } = termSlice

  useAskLeoPageContext(
    React.useMemo(
      () => ({
        title: "Dashboard",
        description: "Term lifecycle overview for scheduling surveys and closed term analytics.",
        suggestions: [
          "Which courses still need evaluations scheduled?",
          "Summarize response rates for the current term",
          "Which faculty scored below threshold last term?",
        ],
      }),
      [],
    ),
  )

  return (
    <div className={cn("flex flex-col gap-3 pb-8", EVAL_DASHBOARD_KPI_A11Y_CLASS)}>
      <PageHeader
        title="Dashboard"
        actions={approach === "quality" && !showNoTerms ? <QualityTermSelector /> : undefined}
        className="pb-0 lg:pb-1"
      />

      <div className="flex flex-col gap-10 px-4 lg:px-6">
        {approach === "lifecycle" ? (
          <EvaluationDashboardLifecycleView
            showNoTerms={showNoTerms}
            currentTerm={currentTerm}
            lastTerm={lastTerm}
            pastTerms={pastTerms}
            upcomingTerms={upcomingTerms}
            futureTerms={futureTerms}
          />
        ) : null}

        {approach === "action" && !showNoTerms ? (
          <EvaluationDashboardActionView currentTerm={currentTerm} lastTerm={lastTerm} />
        ) : null}

        {approach === "analytic" && !showNoTerms ? (
          <EvaluationDashboardAnalyticView
            currentTerm={currentTerm}
            lastTerm={lastTerm}
            pastTerms={pastTerms}
          />
        ) : null}

        {approach === "quality" && !showNoTerms ? (
          <EvaluationDashboardQualityView />
        ) : null}

        {approach === "operations" && !showNoTerms ? (
          <EvaluationDashboardOperationsView />
        ) : null}

        {approach === "operations-v2" && !showNoTerms ? (
          <EvaluationDashboardOperationsV2View
            currentTerm={currentTerm}
            lastTerm={lastTerm}
            upcomingTerms={upcomingTerms}
            futureTerms={futureTerms}
            allTerms={allTerms}
          />
        ) : null}

        {showNoTerms && approach !== "lifecycle" && approach !== "quality" ? (
          <EvaluationDashboardLifecycleView
            showNoTerms
            pastTerms={[]}
            upcomingTerms={[]}
            futureTerms={[]}
          />
        ) : null}

        {!showNoTerms && approach !== "operations-v2" ? (
          <AllTermsSection terms={allTerms} />
        ) : null}
      </div>
    </div>
  )
}
